import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button, EmptyState, Field, Page, Skeleton, StatBlock, compactNumber, formatRelativeTime } from "@cntrl/ui";
import { useCapability } from "../../../app/bandContext.js";
import { useFans, type FanSummary } from "../../../app/hooks/useFans.js";
import { useBroadcasts, type BroadcastSummary } from "../../../app/hooks/useBroadcasts.js";
import { AddFanModal } from "./AddFanModal.js";
import { FanDetailOverlay } from "./FanDetailOverlay.js";
import { EmailStyleModal } from "./EmailStyleModal.js";
import { ImportFansModal } from "./ImportFansModal.js";
import { SignupQrModal } from "./SignupQrModal.js";
import styles from "./FansScreen.module.scss";

/**
 * `/marketing/fans` — the artist marketing-audience CRM: fan directory + stats strip, the
 * Fan Detail overlay, add-fan + email-style modals, and the LOG-ONLY broadcast composer + history. The surface is
 * fans:read gated (owner/AV + management — a fan-PII gate tighter than the Marketing tab; label + content see a
 * "not available" state). Compose + per-broadcast stats are routed modals (deep-linkable); add-fan, fan detail,
 * and email style are transient overlays. v1 broadcasts do not send — the composer says so plainly.
 */
export function FansScreen() {
  const canRead = useCapability("fans:read");
  const canManage = useCapability("fans:manage");
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [openFanId, setOpenFanId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showEmailStyle, setShowEmailStyle] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const fans = useFans(search.trim() || undefined);
  const broadcasts = useBroadcasts();

  if (!canRead) {
    return (
      <Page>
        <EmptyState
          title="Fans aren't available for your role"
          description="The fan list holds personal contact details, so it's limited to owner/artist seats and management."
        />
      </Page>
    );
  }

  const stats = fans.data?.stats;

  return (
    <Page
      title="Fans"
      subtitle="Your marketing audience — list, broadcast, and track growth."
      actions={
        canManage && (
          <>
            <Button variant="ghost" onClick={() => setShowQr(true)}>Signup QR</Button>
            <Button variant="ghost" onClick={() => setShowImport(true)}>Import</Button>
            <Button variant="ghost" onClick={() => setShowEmailStyle(true)}>Email style</Button>
            <Button variant="ghost" onClick={() => void navigate({ to: "/marketing/fans/broadcasts/new" })}>
              Compose broadcast
            </Button>
            <Button variant="primary" onClick={() => setShowAdd(true)}>Add fan</Button>
          </>
        )
      }
    >
      {/* Stats strip*/}
      <section className={styles.stats} aria-label="Fan stats">
        <StatBlock label="Total fans" value={stats?.total ?? null} />
        <StatBlock label="Subscribed" value={stats?.subscribed ?? null} />
        <StatBlock label="SMS reach" value={stats?.smsReach ?? null} />
        <StatBlock label="Opt-outs" value={stats?.optOuts ?? null} />
        <StatBlock label="Avg shows / fan" value={stats?.avgShowsPerFan ?? null} />
      </section>

      {/* Directory */}
      <section className={styles.box} aria-label="Fan directory">
        <header className={styles.head}>
          <h3 className={styles.title}>Directory</h3>
          <div className={styles.searchWrap}>
            <Field
              label="Search"
              placeholder="Name, email, or city"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
            />
          </div>
        </header>

        {fans.isLoading ? (
          <div role="status" aria-live="polite" aria-label="Loading">
            <Skeleton variant="line" width="80%" />
            <Skeleton variant="line" width="60%" />
            <Skeleton variant="line" width="70%" />
          </div>
        ) : fans.isError ? (
          <EmptyState
            title="Couldn't load your fans"
            description="Something went wrong."
            action={<Button variant="ghost" onClick={() => void fans.refetch()}>Retry</Button>}
          />
        ) : (fans.data?.items?.length ?? 0) === 0 ? (
          <EmptyState
            title={search ? "No fans match your search" : "No fans yet"}
            description={search ? undefined : "Add fans manually, or grow your list with the public signup page."}
            action={canManage && !search ? <Button variant="ghost" onClick={() => setShowAdd(true)}>Add your first fan</Button> : undefined}
          />
        ) : (
          <ul className={styles.rows}>
            {(fans.data?.items ?? []).map((f) => (
              <FanRow key={f.fanId} fan={f} onOpen={() => f.fanId && setOpenFanId(f.fanId)} />
            ))}
          </ul>
        )}
      </section>

      {/* Broadcast history*/}
      <section className={styles.box} aria-label="Recent broadcasts">
        <header className={styles.head}>
          <h3 className={styles.title}>Recent broadcasts</h3>
          <span className={styles.logNote}>Log-only — nothing is sent yet</span>
        </header>
        {broadcasts.isLoading ? (
          <Skeleton variant="line" width="60%" />
        ) : broadcasts.isError ? (
          <EmptyState
            title="Couldn't load your broadcasts"
            description="Something went wrong."
            action={<Button variant="ghost" onClick={() => void broadcasts.refetch()}>Retry</Button>}
          />
        ) : (broadcasts.data?.items?.length ?? 0) === 0 ? (
          <EmptyState title="No broadcasts yet" description="Compose one to start a record — v1 logs it without sending." />
        ) : (
          <ul className={styles.rows}>
            {(broadcasts.data?.items ?? []).map((b) => (
              <BroadcastRow
                key={b.broadcastId}
                b={b}
                onOpen={() => b.broadcastId && void navigate({ to: "/marketing/fans/broadcasts/$broadcastId", params: { broadcastId: b.broadcastId } })}
              />
            ))}
          </ul>
        )}
      </section>

      {showAdd && <AddFanModal onClose={() => setShowAdd(false)} onAdded={() => setShowAdd(false)} />}
      {openFanId && <FanDetailOverlay fanId={openFanId} onClose={() => setOpenFanId(null)} />}
      {showEmailStyle && <EmailStyleModal onClose={() => setShowEmailStyle(false)} />}
      {showImport && <ImportFansModal onClose={() => setShowImport(false)} onImported={() => void fans.refetch()} />}
      {showQr && <SignupQrModal onClose={() => setShowQr(false)} />}
    </Page>
  );
}

function FanRow({ fan, onOpen }: { fan: FanSummary; onOpen: () => void }) {
  const name = [fan.firstName, fan.lastName].filter(Boolean).join(" ") || fan.email || "Unnamed fan";
  const place = [fan.city, fan.state].filter(Boolean).join(", ");
  return (
    <li>
      <button type="button" className={styles.row} onClick={onOpen}>
        <span className={styles.rowMain}>
          <span className={styles.rowName}>{name}</span>
          {fan.email && <span className={styles.rowSub}>{fan.email}</span>}
        </span>
        <span className={styles.rowMeta}>
          {place && <span className={styles.rowSub}>{place}</span>}
          <span className={styles.badges}>
            {fan.optedOut ? (
              <span className={styles.badgeMuted}>Opted out</span>
            ) : (
              <>
                {fan.emailSubscribed && <span className={styles.badge}>Email</span>}
                {fan.smsOptIn && <span className={styles.badge}>SMS</span>}
              </>
            )}
          </span>
          <span className={styles.rowShows}>{compactNumber(fan.showsAttended ?? 0)} shows</span>
        </span>
      </button>
    </li>
  );
}

function BroadcastRow({ b, onOpen }: { b: BroadcastSummary; onOpen: () => void }) {
  return (
    <li>
      <button type="button" className={styles.row} onClick={onOpen}>
        <span className={styles.rowMain}>
          <span className={styles.rowName}>{b.subject || (b.channel === "sms" ? "SMS broadcast" : "Broadcast")}</span>
          <span className={styles.rowSub}>{(b.channel ?? "").toUpperCase()} · {b.audienceKind}</span>
        </span>
        <span className={styles.rowMeta}>
          <span className={styles.badgeMuted}>{b.status}</span>
          <span className={styles.rowSub}>{formatRelativeTime(b.createdOn)}</span>
        </span>
      </button>
    </li>
  );
}
